import type { ActionDefinition } from '@command-center/action-sdk';

export type DataSourceType = 'airtable' | 'supabase' | 'custom' | 'mock';
export type FieldType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'url'
  | 'email'
  | 'select'
  | 'multi-select'
  | 'relation'
  | 'currency'
  | 'markdown'
  | 'json';

export interface FieldDefinition {
  id: string;
  label: string;
  type: FieldType;
  sourceField?: string;
  required?: boolean;
  readonly?: boolean;
  options?: Array<{ label: string; value: string; color?: string }>;
}

export interface FilterDefinition {
  id: string;
  label: string;
  fieldId: string;
  operator: 'equals' | 'not-equals' | 'contains' | 'starts-with' | 'is-empty' | 'is-not-empty';
  value?: unknown;
}

export interface DetailViewDefinition {
  titleFieldId?: string;
  sections: Array<{
    id: string;
    label: string;
    fieldIds: string[];
  }>;
}

export interface TableDefinition {
  id: string;
  label: string;
  source: DataSourceType;
  connectorId?: string;
  tableName?: string;
  baseIdEnv?: string;
  defaultView?: string;
  fields: FieldDefinition[];
  filters?: FilterDefinition[];
  rowActions?: ActionDefinition[];
  detailView?: DetailViewDefinition;
}

export interface ViewDefinition {
  id: string;
  label: string;
  tableId: string;
  filters?: FilterDefinition[];
  visibleFieldIds?: string[];
  sort?: Array<{ fieldId: string; direction: 'asc' | 'desc' }>;
}

export interface DataAdapter<TRecord = Record<string, unknown>> {
  source: DataSourceType;
  list: (table: TableDefinition, query?: DataQuery) => Promise<DataPage<TRecord>>;
  get?: (table: TableDefinition, id: string) => Promise<TRecord | null>;
}

export interface DataQuery {
  search?: string;
  filters?: Record<string, unknown>;
  cursor?: string;
  limit?: number;
}

export interface DataPage<TRecord = Record<string, unknown>> {
  items: TRecord[];
  nextCursor?: string | null;
  total?: number;
}

export function createTableDefinition(table: TableDefinition): TableDefinition {
  if (!table.id.trim()) throw new Error('Table id cannot be empty.');
  if (table.fields.length === 0) throw new Error(`Table ${table.id} must define at least one field.`);
  return table;
}

export function createViewDefinition(view: ViewDefinition): ViewDefinition {
  if (!view.id.trim()) throw new Error('View id cannot be empty.');
  return view;
}
